import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Cards from './pages/Cards';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminOrders from './pages/admin/AdminOrders';
import AdminBillings from './pages/admin/AdminBillings';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminCustomerDetail from './pages/admin/AdminCustomerDetail';
import axios from 'axios';
import './App.css';

function AppContent({
  user,
  userToken,
  login,
  logout,
  adminUser,
  adminToken,
  adminLogin,
  adminLogout,
  isSidebarCollapsed,
  toggleSidebar
}) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminRoute) {
      if (adminToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
      } else {
        delete axios.defaults.headers.common['Authorization'];
      }
    } else {
      if (userToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
      } else {
        delete axios.defaults.headers.common['Authorization'];
      }
    }
  }, [isAdminRoute, adminToken, userToken]);

  useEffect(() => {
    if (!isAdminRoute) {
      document.body.classList.remove('admin-sidebar-collapsed');
      return;
    }

    if (isSidebarCollapsed) {
      document.body.classList.add('admin-sidebar-collapsed');
    } else {
      document.body.classList.remove('admin-sidebar-collapsed');
    }

    return () => {
      document.body.classList.remove('admin-sidebar-collapsed');
    };
  }, [isAdminRoute, isSidebarCollapsed]);

  return (
    <>
      {!isAdminRoute && <Navbar user={user} logout={logout} />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/login" element={!user ? <Login login={login} /> : <Navigate to="/" />} />
        <Route path="/register" element={!user ? <Register /> : <Navigate to="/" />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" />} />
        <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/" />} />
        <Route path="/profile" element={user ? <Profile user={user} /> : <Navigate to="/login" />} />
        <Route path="/orders" element={user ? <Orders /> : <Navigate to="/login" />} />
        <Route path="/orders/:orderId" element={user ? <OrderDetail /> : <Navigate to="/login" />} />
        <Route path="/cards" element={user ? <Cards /> : <Navigate to="/login" />} />
        <Route
          path="/admin/login"
          element={!adminUser ? <AdminLogin login={adminLogin} /> : <Navigate to="/admin/dashboard" />}
        />
        <Route
          path="/admin/dashboard"
          element={
            adminUser ? (
              <AdminDashboard
                admin={adminUser}
                logout={adminLogout}
                isSidebarCollapsed={isSidebarCollapsed}
                toggleSidebar={toggleSidebar}
              />
            ) : (
              <Navigate to="/admin/login" />
            )
          }
        />
        <Route
          path="/admin/products"
          element={
            adminUser ? (
              <AdminProducts
                admin={adminUser}
                logout={adminLogout}
                isSidebarCollapsed={isSidebarCollapsed}
                toggleSidebar={toggleSidebar}
              />
            ) : (
              <Navigate to="/admin/login" />
            )
          }
        />
        <Route
          path="/admin/orders"
          element={
            adminUser ? (
              <AdminOrders
                admin={adminUser}
                logout={adminLogout}
                isSidebarCollapsed={isSidebarCollapsed}
                toggleSidebar={toggleSidebar}
              />
            ) : (
              <Navigate to="/admin/login" />
            )
          }
        />
        <Route
          path="/admin/billings"
          element={
            adminUser ? (
              <AdminBillings
                admin={adminUser}
                logout={adminLogout}
                isSidebarCollapsed={isSidebarCollapsed}
                toggleSidebar={toggleSidebar}
              />
            ) : (
              <Navigate to="/admin/login" />
            )
          }
        />
        <Route
          path="/admin/customers"
          element={
            adminUser ? (
              <AdminCustomers
                admin={adminUser}
                logout={adminLogout}
                isSidebarCollapsed={isSidebarCollapsed}
                toggleSidebar={toggleSidebar}
              />
            ) : (
              <Navigate to="/admin/login" />
            )
          }
        />
        <Route
          path="/admin/customers/:customerId"
          element={
            adminUser ? (
              <AdminCustomerDetail
                admin={adminUser}
                logout={adminLogout}
                isSidebarCollapsed={isSidebarCollapsed}
                toggleSidebar={toggleSidebar}
              />
            ) : (
              <Navigate to="/admin/login" />
            )
          }
        />
      </Routes>
      {!isAdminRoute && <Footer />}
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [userToken, setUserToken] = useState('');
  const [adminUser, setAdminUser] = useState(null);
  const [adminToken, setAdminToken] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminSidebarCollapsed, setIsAdminSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('admin_sidebar_collapsed') === 'true';
  });

  // เมื่อแอปโหลดครั้งแรก ให้อ่าน token/ข้อมูลผู้ใช้ที่เคยเก็บไว้
  useEffect(() => {
    // ------- ฝั่งลูกค้า -------
    const customerToken = localStorage.getItem('token');
    if (customerToken) {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      }
      setUserToken(customerToken);
    }

    // ------- ฝั่งแอดมิน -------
    const storedAdminToken = localStorage.getItem('admin_token');
    if (storedAdminToken) {
      const adminData = localStorage.getItem('admin_user');
      if (adminData) {
        setAdminUser(JSON.parse(adminData));
      }
      setAdminToken(storedAdminToken);
      
      // Set axios headers immediately for admin routes
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedAdminToken}`;
      }
    } else if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
      // Clear headers if no admin token on admin route
      delete axios.defaults.headers.common['Authorization'];
    }

    // โหลดเสร็จแล้ว ปล่อยให้ UI แสดงผล
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('admin_sidebar_collapsed', isAdminSidebarCollapsed ? 'true' : 'false');
    }
  }, [isAdminSidebarCollapsed]);

  const login = (userData, token) => {
    setUser(userData);
    setUserToken(token);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('userChanged', { detail: { user: userData } }));
    }
  };

  const logout = () => {
    setUser(null);
    setUserToken('');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('userChanged', { detail: { user: null } }));
    }
  };

  const adminLogin = (adminData, token) => {
    setAdminUser(adminData);
    setAdminToken(token);
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_user', JSON.stringify(adminData));
    // Set axios headers immediately for admin routes
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const adminLogout = () => {
    setAdminUser(null);
    setAdminToken('');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    delete axios.defaults.headers.common['Authorization'];
  };

  const toggleSidebar = () => {
    setIsAdminSidebarCollapsed((prev) => !prev);
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppContent
        user={user}
        userToken={userToken}
        login={login}
        logout={logout}
        adminUser={adminUser}
        adminToken={adminToken}
        adminLogin={adminLogin}
        adminLogout={adminLogout}
        isSidebarCollapsed={isAdminSidebarCollapsed}
        toggleSidebar={toggleSidebar}
      />
    </Router>
  );
}

export default App;

